"use client";

import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { getCarouselItems } from "@/actions/content.action";
import ArrowIcon from "@/components/icons/arrow";
import { cn } from "@/lib/utils";

const TIMER_DURATION = 10_000;
const TICK_INTERVAL = 50;
const MAX_PROGRESS = 100;
const DEGREES_FULL = 360;

const MiniCarousel = () => {
  const [activeItem, setActiveItem] = useState(0);
  const [progress, setProgress] = useState(0);

  const { data } = useQuery({
    queryFn: async () => {
      return await getCarouselItems();
    },
    queryKey: ["carousel-items"],
  });

  const its = useMemo(
    () =>
      (data || []).map((item) => {
        if (item.referenceType === "banner") {
          return {
            actionText: item.banner?.buttonText || "Devamı",
            actionUrl: item.banner?.actionUrl || "#",
            coverImage: item.banner?.smallImage,
            id: item.banner?.id || Math.random().toString(),
            lead: item.banner?.subtitle || "",
            thumbnailUrl: item.banner?.largeImage,
            title: item.banner?.title || "",
          };
        }
        return {
          actionText: "Devamı",
          actionUrl: item.content?.slug ? `/${item.content.slug}` : "#",
          coverImage: item.content?.coverImageUrl,
          id: item.content?.id || Math.random().toString(),
          lead: item.content?.lead || "",
          thumbnailUrl: item.content?.coverImageUrl || item.content?.thumbnailUrl,
          title: item.content?.title || "",
        };
      }),
    [data],
  );

  const itsRef = useRef(its);
  itsRef.current = its;

  const pendingSelectRef = useRef<number | null>(null);

  useEffect(() => {
    let startTime = Date.now();
    setProgress(0);

    const interval = setInterval(() => {
      if (pendingSelectRef.current !== null) {
        setActiveItem(pendingSelectRef.current);
        pendingSelectRef.current = null;
        startTime = Date.now();
        setProgress(0);
        return;
      }

      if (itsRef.current.length === 0) {
        return;
      }

      const elapsed = Date.now() - startTime;
      const next = Math.min((elapsed / TIMER_DURATION) * MAX_PROGRESS, MAX_PROGRESS);
      setProgress(next);

      if (next >= MAX_PROGRESS) {
        setActiveItem((prev) => (prev + 1) % itsRef.current.length);
        startTime = Date.now();
        setProgress(0);
      }
    }, TICK_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  const handleSelect = (index: number) => {
    pendingSelectRef.current = index;
  };

  const degrees = (progress / MAX_PROGRESS) * DEGREES_FULL;

  return (
    <div className="aspect-4/5 z-0 w-full relative">
      {its.map((item, index) => (
        <div
          className={`absolute inset-0 rounded-2xl flex flex-col justify-center px-4 transition-opacity duration-500 ease-in-out ${
            activeItem === index
              ? "opacity-100 z-10"
              : "opacity-0 z-0 pointer-events-none"
          }`}
          key={item.id}
          style={{
            backgroundImage: `radial-gradient(circle, rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 100%), url(${item.coverImage})`,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            height: "100%",
            width: "100%",
          }}
        >
          <div className="w-full shrink-0 flex flex-col justify-end gap-4">
            <h2 className={cn("text-[22px] leading-8 font-bold text-white")}>
              {item.title}
            </h2>
            <p className="text-sm leading-5 font-medium text-white line-clamp-2">
              {item.lead}
            </p>
            <Link
              className="w-fit text-white hover:bg-brand-blue-600 bg-brand-blue-500 flex flex-row gap-2 rounded-full px-4 py-2 items-center justify-center text-lg font-medium"
              href={item.actionUrl || "#"}
            >
              Devamı <ArrowIcon className="stroke-white" />
            </Link>
          </div>
        </div>
      ))}
      <div
        className="absolute bottom-0 p-4 pt-2 z-20 w-full"
        id="carousel-indicators"
      >
        <div className="w-full flex flex-row gap-2 border-t pt-3 border-neutral-200">
          {its.map((item, index) => (
            <div
              className={cn("size-14 bg-white rounded-lg cursor-pointer")}
              // biome-ignore lint/suspicious/noArrayIndexKey: its ok
              key={index}
              onClick={() => handleSelect(index)}
              role="none"
            >
              <div
                className={`inset-0 rounded-lg ${
                  activeItem === index
                    ? "border-2 md:border-4 border-transparent"
                    : ""
                }`}
                style={{
                  backgroundClip: "padding-box, border-box",
                  backgroundImage:
                    activeItem === index
                      ? `url(${item.coverImage}), conic-gradient(from 0deg, var(--brand-blue) 0deg ${degrees}deg, #FFFFFF ${degrees}deg ${DEGREES_FULL}deg)`
                      : `url(${item.thumbnailUrl})`,
                  backgroundOrigin: "padding-box, border-box",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  height: "100%",
                  width: "100%",
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MiniCarousel;
